import { Role } from 'auth';
import classNames from 'classnames';
import { useCallback, useContext, useMemo } from 'react';
import {
  createSearchParams,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { queryClient } from '@/modules/common/query.ts';
import '@/modules/common/styles/common.css';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { CURRENT_ENTITY_QUERY_KEY } from '@/modules/entity/queries/entity.tsx';
import { EntityItemFullResponse } from '@/modules/entity/types/EntityItemResponse.ts';
import { InvestorDashboardWrapperOutletContext } from '@/modules/investor-dashboard/components/InvestorDashboardWrapper.tsx';
import { SubscribeModal } from '@/modules/investor-dashboard/components/modals/SubscribeModal.tsx';
import { TransferModal } from '@/modules/investor-dashboard/components/modals/TransferModal.tsx';
import { useCurrentUserInvestmentsQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/transactions.tsx';
import { investorDashboardPaths } from '@/modules/investor-dashboard/routes.tsx';
import {
  BodyCell,
  Button,
  DropdownMenu,
  EmptyListState,
  ModalProviderContext,
  PageLayout,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { DotsHorizontalIcon } from 'ui/icons';
import { goodFormatUSDCurrency } from 'ui/utils';
import {
  GetEntityShareholdersResponse,
  RegisterClassWithCalculateResponse,
  RegisterWithInvestmentResponse,
  TransactionType,
} from 'api';

type TypeHeadMyInvestments =
  | 'Register'
  | 'Class'
  | 'Price'
  | 'Holdings'
  | 'Value'
  | 'Performance'
  | 'Div. reinvestment'
  | 'Actions';

const headItems: TableHeadColumn<TypeHeadMyInvestments>[] = [
  {
    title: 'Register',
    className: 'font-normal min-w-[170px]',
  },
  {
    title: 'Class',
    className: 'font-normal min-w-[80px]',
  },
  {
    title: 'Price',
    className: 'font-normal',
  },
  {
    title: 'Holdings',
    className: 'font-normal',
  },
  {
    title: 'Value',
    className: 'font-normal',
  },
  {
    title: 'Performance',
    className: 'font-normal',
  },
  {
    title: 'Actions',
    className: 'font-normal min-w-[235px] w-[235px]',
  },
];

const InvestorMyInvestments = () => {
  const { register_id } = useOutletContext<InvestorDashboardWrapperOutletContext>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { openModal } = useContext(ModalProviderContext);
  const entity = queryClient.getQueryData<EntityItemFullResponse>([CURRENT_ENTITY_QUERY_KEY]);
  const entityShareholders = queryClient.getQueryData<GetEntityShareholdersResponse>([
    CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY,
    entity?.entity?.id,
  ]);
  const { hasNextPage, fetchNextPage, data, isLoading, isFetching, isError } =
    useCurrentUserInvestmentsQuery({
      page: 1,
      page_size: 15,
      register_id: Number(register_id),
    });

  const investments = (data?.pages || []).flatMap(({ data }) => data);

  const handleOpenTransferModal = useCallback(
    (
        registerClass: RegisterClassWithCalculateResponse,
        register: RegisterWithInvestmentResponse,
        shareholderId: number,
      ) =>
      () => {
        const { id, current_price_per_share, class_name } = registerClass;
        openModal(TransferModal, {
          register_class_id: id,
          register_class_name: class_name,
          from_shareholder_id: shareholderId,
          price_per_share: current_price_per_share,
          register_id: register.id,
        });
      },
    [openModal],
  );

  const handleOpenSubscribeModalSubscriptionType = useCallback(
    (
        registerClass: RegisterClassWithCalculateResponse,
        register: RegisterWithInvestmentResponse,
        shareholderId: number,
      ) =>
      () => {
        const { id, current_price_per_share, class_name } = registerClass;
        openModal(SubscribeModal, {
          register_id: register.id,
          register_class_id: id,
          price_per_share: current_price_per_share,
          shareholder_id: shareholderId,
          register_class_name: register.name + ' - ' + class_name,
          mode: TransactionType.Subscription,
        });
      },
    [openModal],
  );

  const handleOpenSubscribeModalRedemptionType = useCallback(
    (
        registerClass: RegisterClassWithCalculateResponse,
        register: RegisterWithInvestmentResponse,
        shareholderId: number,
      ) =>
      () => {
        const { id, current_price_per_share, class_name } = registerClass;
        openModal(SubscribeModal, {
          register_id: register.id,
          register_class_id: id,
          price_per_share: current_price_per_share,
          shareholder_id: shareholderId,
          register_class_name: register.name + ' - ' + class_name,
          mode: TransactionType.Redemption,
        });
      },
    [openModal],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    const result: TableBodyColumn[] = [];

    investments.forEach(register => {
      if (register?.register_classes_info) {
        result.push(
          ...(register?.register_classes_info || []).map(registerClass => {
            const shareholderId =
              entityShareholders?.shareholders.find(
                ({ entity_id }) => entity_id === entity?.entity.id,
              )?.id || 0;

            const body: BodyCell[] = [
              { cell: register.name },
              { cell: registerClass.class_name },
              {
                cell: `${goodFormatUSDCurrency(
                  Math.round(registerClass.current_price_per_share * 100) / 100,
                )}`,
              },
              {
                cell: (
                  <>
                    {formatNumberWithCommas(
                      registerClass.number_of_units
                        ? +registerClass.number_of_units?.toFixed(4)
                        : 0,
                    )}{' '}
                    units/shares
                  </>
                ),
              },
              {
                cell: (
                  <>
                    {goodFormatUSDCurrency(
                      Math.round(
                        registerClass.current_price_per_share *
                          (registerClass.number_of_units || 0) *
                          100,
                      ) / 100,
                    )}
                  </>
                ),
              },
              {
                cell: (
                  <div
                    className={classNames('flex text-[14px] leading-[22px]', {
                      'text-error-red': registerClass.change && registerClass.change < 0,
                      'text-success-green': registerClass.change && registerClass.change >= 0,
                    })}
                  >
                    {formatNumberWithCommas(registerClass.change)}%
                  </div>
                ),
              },
              {
                cell: (
                  <div className="flex items-center justify-start gap-1 select-none">
                    <div className="flex flex-wrap gap-1">
                      <Button
                        className="w-[79px]"
                        variant="plain"
                        onClick={handleOpenSubscribeModalSubscriptionType(
                          registerClass,
                          register,
                          shareholderId,
                        )}
                      >
                        Subscribe
                      </Button>
                      <Button
                        className="w-[79px]"
                        variant="plain"
                        onClick={handleOpenSubscribeModalRedemptionType(
                          registerClass,
                          register,
                          shareholderId,
                        )}
                      >
                        Redeem
                      </Button>
                    </div>

                    <div className="w-[40px] h-[40px] flex items-center justify-center ">
                      <DropdownMenu
                        items={[
                          {
                            label: 'Transfer',
                            onClick: handleOpenTransferModal(
                              registerClass,
                              register,
                              shareholderId,
                            ),
                          },
                          {
                            label: 'Activity',
                            onClick: () =>
                              navigate(
                                {
                                  pathname: investorDashboardPaths.static.activity,
                                  search: createSearchParams(params).toString(),
                                },
                                {
                                  replace: false,
                                },
                              ),
                          },
                          {
                            label: 'Certificates',
                            onClick: () => {
                              navigate(
                                {
                                  pathname: investorDashboardPaths.static.certificates,
                                  search: createSearchParams(params).toString(),
                                },
                                {
                                  replace: false,
                                },
                              );
                            },
                          },
                          // {
                          //   label: 'Transactions history',
                          //   onClick: () =>
                          //     navigate({
                          //       pathname: investorDashboardPaths.static.transactionsHistory,
                          //       search: createSearchParams(params).toString(),
                          //     }),
                          // },
                        ]}
                        button={
                          <button className="flex items-center justify-center">
                            <img src={DotsHorizontalIcon} alt="Loading..." />
                          </button>
                        }
                        placement="left-start"
                        className="w-[180px]"
                      />
                    </div>
                  </div>
                ),
              },
            ];

            return { cells: body };
          }),
        );
      }
    });

    return result;
  }, [
    entity?.entity.id,
    entityShareholders?.shareholders,
    handleOpenSubscribeModalRedemptionType,
    handleOpenSubscribeModalSubscriptionType,
    handleOpenTransferModal,
    investments,
    navigate,
    params,
  ]);

  return (
    <PageLayout title="My investments" headerClassname="!border-none" contentClassName="!p-0">
      {isFetching || isLoading || bodyItems?.length > 0 ? (
        <Table
          className="my-0"
          withRounded={false}
          withBorderX={false}
          headItems={headItems}
          items={bodyItems}
          isLoading={isFetching || isLoading}
          isError={isError}
          hasNextPageInfinite={hasNextPage}
          fetchNextPageInfinite={fetchNextPage}
          blockIdInfinite="scrollable"
        />
      ) : (
        <EmptyListState
          title="No investments yet"
          description="If you have submitted a subscription/application and you don't see it here, it means it's awaiting an approval from the issuer."
          isLoading={isLoading}
          isSearchUsed={false}
          itemsCount={bodyItems.length}
        />
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(InvestorMyInvestments, Role.SR_INVESTOR);
